<template>
  <router-view :key="$route.path"></router-view>

  <!-- Overlay globali e barra SIP inline sono nascosti quando la window
       ospita la route /sip-bar: in quel caso l'unico contenuto deve essere
       la pagina del popup (vedi pages/SipBar/SipBar.vue). -->
  <template v-if="!isSipBarRoute">
    <template v-if="canShowRibbon">
      <EnvironmentRibbon />
    </template>
    <ConfirmDialog />
    <Confirm />
    <p v-if="isInactiveTooLong">Sei stato inattivo troppo a lungo. Sessione scaduta.</p>
    <Alert />
    <!-- Fuori dal popup SIP: quella window non deve aprire una seconda connessione socket -->
    <AppSocketListener />

    <div v-if="alertStore.visible" class="w-auto fixed z-50" :class="dynamicClass">
      <v-alert
        :icon="alertStore.icon"
        density="compact"
        variant="elevated"
        :type="alertStore.type"
        :title="alertStore.title"
        :text="alertStore.message"
      ></v-alert>
    </div>

    <!-- Banner chiamata in arrivo (visibile solo nelle view, non nel popup).
         Mostrato quando il popup SIP è in background e broadcast riceve
         incoming-call: segnala all'operatore nella tab view. Il bottone
         "Rispondi" invia il comando answer al popup owner e lo porta in
         primo piano. -->
    <div
      class="fixed z-50"
      style="top: 16px; left: 50%; transform: translateX(-50%)"
    >
      <SipIncomingCallBanner
        :phone-number="bannerPhoneNumber"
        :caller-line="bannerCallerLine"
        :lead-url="bannerLeadUrl"
        :error="bannerError"
        @answer="onAnswerIncoming"
        @dismiss="onDismissIncoming"
        @open-lead="onOpenIncomingLead"
      />
    </div>
  </template>
</template>

<script setup lang="ts">
import EnvironmentRibbon from "./components/common/EnvironmentRibbon.vue";
import Confirm from "@/components/common/Confirm.vue";
import SipIncomingCallBanner from "@/components/SIPIntegration/components/SipIncomingCallBanner.vue";
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAlert } from "@/store/alert";
import { useUser } from "@/store/user";
import { useSipStore } from "@/store/sip";

import { useSipPopupManager } from "@/composables/useSipPopupManager";
import { useSipBroadcast } from "@/components/SIPIntegration/composables/useSipBroadcast";
import { useSipCommands } from "@/composables/useSipCommands";
import { useSipIncomingNotification } from "@/composables/useSipIncomingNotification";
import { sipTelemetry } from "@/sip/sipTelemetry";
import { holdMainPresence } from "@/sip/mainPresence";

import { useReloadOnInactivity } from "@/composables/useReloadOnInactivity";
import Alert from "./components/common/Alert.vue";
import ConfirmDialog from "./components/common/dialogs/confirmDialog/ConfirmDialog.vue";
import AppSocketListener from "@/components/common/socket/AppSocketListener.vue";

// Determina se la window corrente è il popup SIP. Si basa sul pathname iniziale
// (sincrono, affidabile al primo render) e poi sulla route reattiva: il popup
// non condivide overlay/shell con la main app.
const isSipBarWindow = window.location.pathname.startsWith("/sip-bar");

// Reload da inattività: SOLO nella main app. Nel popup l'utente non interagisce
// mai (lavora nelle view), quindi il timer scadeva sistematicamente e ricaricava
// la finestra che ospita la sessione SIP: registrazione buttata giù ogni 15
// minuti e chiamata tagliata se il tick cadeva durante lo squillo, quando
// isInCall è ancora false. Il popup ha già la propria igiene di ciclo di vita
// (heartbeat main-alive + Web Lock, vedi SipBar.vue). GAIA-2674.
const inactivity = isSipBarWindow ? null : useReloadOnInactivity();
const isInactiveTooLong = computed(() => inactivity?.isInactiveTooLong.value ?? false);

const userStore = useUser();
const alertStore = useAlert();
const sipStore = useSipStore();
const route = useRoute();
const router = useRouter();

// Una chiamata SIP è "in volo" se sta squillando, è in corso, in attesa di
// risposta o in ringback. Non vogliamo aprire la window popup durante questi
// stati: il trasferimento di leadership cederebbe la sessione SIP al popup e
// l'inline farebbe deregister → la chiamata audio si interromperebbe.
const hasActiveCall = computed(
  () =>
    sipStore.isInCall ||
    !!sipStore.incomingCall ||
    sipStore.isRinging ||
    sipStore.isOutgoingRinging
);

const canShowRibbon = computed((): boolean => {
  return ["development", "staging"].indexOf(import.meta.env.MODE) >= 0;
});

// isSipBarWindow è dichiarata in testa al setup: serve già al gate del reload da
// inattività. Qui si aggiunge la variante reattiva basata sulla route.
const isSipBarRoute = computed(() => route.name === "sip-bar" || isSipBarWindow);

// useSipPopupManager va inizializzato SOLO nella main app: il popup non ha
// bisogno di gestire se stesso, e la sua chiamata avvierebbe handler heartbeat
// inutili nel suo stesso BroadcastChannel.
const popupManager = isSipBarWindow ? null : useSipPopupManager();

// useSipCommands va inizializzato solo nelle view: invia comandi al popup owner
// via BroadcastChannel. Nel popup stesso il comando non servirebbe (è lui l'owner).
const sipCommands = isSipBarWindow ? null : useSipCommands();

// Notifica inbound background-proof: attivata SOLO nelle view (non nel popup).
// Ascolta incoming-call su BroadcastChannel e attiva system notification +
// audio + title-flash + banner in-app. La permission viene richiesta alla prima
// gesture utente per rispettare i requisiti browser di user-activation.
const incomingNotification = isSipBarWindow
  ? null
  : useSipIncomingNotification();
// Numero della chiamata in arrivo per il banner in-app (readonly dal composable).
const incomingPhoneNumber = incomingNotification?.incomingPhoneNumber ?? null;
// Flag di chiusura manuale del banner — si azzera a ogni nuova chiamata.
const incomingBannerDismissed = ref(false);
watch(
  () => incomingPhoneNumber?.value,
  (n) => {
    if (n) incomingBannerDismissed.value = false;
  },
);
// Numero passato al banner: null quando dismissato o nessuna chiamata in arrivo.
const bannerPhoneNumber = computed(() =>
  incomingPhoneNumber && !incomingBannerDismissed.value
    ? (incomingPhoneNumber.value ?? null)
    : null,
);
// Scheda del chiamante, quando è un contatto già presente in Gaia. Prima di
// GAIA-2719 questa informazione restava prigioniera della window della barra:
// l'operatore doveva cercare il numero a mano.
// Su quale linea è entrata la chiamata: il centralino la dichiara nel
// display-name dell'INVITE, che vive nella window della barra. Senza questo
// giro l'informazione resterebbe là, e la tab in cui l'operatore lavora
// mostrerebbe un numero senza dire per quale servizio sta squillando.
const incomingCallerLine = incomingNotification?.incomingCallerLine ?? null;
const bannerCallerLine = computed(() =>
  bannerPhoneNumber.value ? (incomingCallerLine?.value ?? null) : null,
);
const incomingLeadUrl = incomingNotification?.incomingLeadUrl ?? null;
const bannerLeadUrl = computed(() =>
  bannerPhoneNumber.value ? (incomingLeadUrl?.value ?? null) : null,
);
// Il "Rispondi" non ha risposto: il banner deve tornare visibile e dirlo. Il
// dismiss fatto al click va annullato, altrimenti l'errore resterebbe nascosto
// proprio nella tab da cui l'operatore ha cliccato (GAIA-2664).
const incomingError = incomingNotification?.incomingError ?? null;
const bannerError = computed(() => incomingError?.value ?? null);
watch(
  () => incomingError?.value,
  (message) => {
    if (message) incomingBannerDismissed.value = false;
  },
);

function onAnswerIncoming(): void {
  sipCommands?.answer();
  popupManager?.focusPopup();
  incomingBannerDismissed.value = true;
}

// Apre la scheda nella tab corrente: è la stessa navigazione che faceva la
// barra inline prima che diventasse popup-only, e non apre finestre non richieste.
function onOpenIncomingLead(url: string): void {
  incomingBannerDismissed.value = true;
  router.push(url);
}

function onDismissIncoming(): void {
  incomingBannerDismissed.value = true;
}
const isPopupAlive = computed(() => popupManager?.isPopupAlive.value ?? false);

// Eleggibilità all'apertura del popup SIP. Condizione: utente è teleseller e
// non ha pending agreements (stesso check che governa la barra inline).
const sipEligible = computed(
  () =>
    userStore.isTeleseller && userStore.missingAgreements.length === 0
);

// Auto-open: alla prima gesture utente (click/keypress) apriamo il popup se
// l'utente è teleseller e non c'è già un popup vivo. Serve a evitare il popup
// blocker: window.open programmatico al login fallirebbe per assenza di
// user-activation.
let autoOpenCancel: (() => void) | null = null;

watch(
  sipEligible,
  (eligible) => {
    if (autoOpenCancel) {
      autoOpenCancel();
      autoOpenCancel = null;
    }
    if (!eligible || !popupManager) return;
    if (isPopupAlive.value) return;
    autoOpenCancel = popupManager.enableAutoOpenOnGesture(
      // Il listener resta armato anche se la condition è false: durante una
      // chiamata in corso non apriamo, ma appena termina (hasActiveCall torna
      // false) la prima gesture successiva farà partire il popup.
      () =>
        sipEligible.value && !isPopupAlive.value && !hasActiveCall.value
    );
  },
  { immediate: true }
);

// Auto-close: se l'utente cambia ruolo e perde l'eligibilità (es. passa da
// Teleselling a BackofficeUser), chiudiamo il popup. Il manager pubblica
// popup-shutdown su BroadcastChannel così funziona anche dopo un F5 della
// main app, quando il riferimento Window al popup è stato perso.
watch(sipEligible, (eligible, previous) => {
  if (previous && !eligible && popupManager) {
    popupManager.closePopup("role-changed");
  }
});

// Caso speciale post-reload: il role switch in RoleList.vue fa
// window.location.replace("/"), quindi la main app riparte da zero. Il watch
// falling-edge sopra non scatta perché previous è undefined al primo trigger.
// Ma il popup è sopravvissuto al reload (è una window separata) e continua a
// pubblicare heartbeat. Quando lo rileviamo, se il nuovo ruolo non è più
// teleseller, lo chiudiamo subito.
watch(isPopupAlive, (alive) => {
  if (!alive) return;
  if (!sipEligible.value && popupManager) {
    popupManager.closePopup("role-changed");
  }
});

// Heartbeat "main-alive": ogni tab della main app pubblica un battito ogni
// 2s, indipendentemente da chi è leader SIP. Il popup ascolta: se per N
// secondi nessuna main pubblica, vuol dire che tutte le tab gaia sono state
// chiuse → il popup si autochiude (vedi SipBar.vue). Non viene pubblicato
// dalla window popup stessa, che è discriminata via isSipBarWindow.
const mainAliveBroadcast = isSipBarWindow ? null : useSipBroadcast();
const mainWindowId =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `main-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
let mainAliveIntervalId: number | null = null;
let lastMainAliveTickAt = Date.now();
let releasePresence: (() => void) | null = null;
// Throttling già confermato in prod: ne segnaliamo UNO per sessione/tab (segnale di
// prevalenza) invece di floodare Sentry a ogni battito perso.
let throttleReportedThisSession = false;

onMounted(() => {
  // Chiede il permesso Notification alla prima gesture utente (click/tap) nella
  // view: window.Notification.requestPermission richiede user-activation in tutti
  // i browser moderni. Un ascoltatore one-shot è sufficiente.
  if (incomingNotification) {
    const onFirstGesture = () => {
      incomingNotification.requestPermission();
      document.removeEventListener("pointerdown", onFirstGesture, {
        capture: true,
      });
    };
    document.addEventListener("pointerdown", onFirstGesture, {
      capture: true,
      once: true,
    });
  }

  if (!mainAliveBroadcast) return;
  // Tiene un Web Lock di presenza per tutta la vita della tab main: segnale
  // non-throttlabile che permette al popup di distinguere "main viva ma in
  // background" da "main chiusa" (vedi mainPresence.ts / SipBar.vue).
  releasePresence = holdMainPresence();
  // Battito immediato così il popup non aspetta fino a 2s per il primo segnale.
  mainAliveBroadcast.postMainAlive(mainWindowId);
  lastMainAliveTickAt = Date.now();
  mainAliveIntervalId = window.setInterval(() => {
    const now = Date.now();
    const delta = now - lastMainAliveTickAt;
    lastMainAliveTickAt = now;
    // Se il delta supera abbondantemente i 2s previsti, il timer è stato
    // throttlato/congelato dal browser (tab in background ≥5min su Chromium):
    // è la firma del bug "la barra si chiude da sola". 8s = ~4 battiti persi.
    if (delta > 8_000 && !throttleReportedThisSession) {
      throttleReportedThisSession = true;
      sipTelemetry.mainAliveThrottled({
        gapMs: delta,
        visibility: document.visibilityState,
      });
    }
    mainAliveBroadcast.postMainAlive(mainWindowId);
  }, 2_000);
});

onBeforeUnmount(() => {
  if (autoOpenCancel) {
    autoOpenCancel();
    autoOpenCancel = null;
  }
  if (mainAliveIntervalId !== null) {
    clearInterval(mainAliveIntervalId);
    mainAliveIntervalId = null;
  }
  if (releasePresence) {
    releasePresence();
    releasePresence = null;
  }
});
</script>

<style lang="postcss">
.v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line {
  @apply mx-2 text-gray-800 font-medium text-sm cursor-pointer;
}

.v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line:hover {
  @apply bg-[#F2F8FF] text-primary font-semibold cursor-pointer;
}

.secondary.v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line:hover {
  @apply bg-[#FFF6ED] text-orange-600 font-semibold cursor-pointer;
}
</style>
